/**
 * Pressure from force and area, and hydrostatic pressure in a fluid at rest.
 *
 *   P = F / A                    pressure is force spread over an area (1 Pa = 1 N/m²)
 *   P_gauge = ρ g h              extra pressure at depth h in a fluid of density ρ
 *   P_abs = P₀ + ρ g h           absolute pressure: add the pressure on the surface
 *
 * Gauge pressure is measured relative to the atmosphere; absolute pressure relative to a
 * vacuum. SI units; a standard atmosphere is 101 325 Pa. Incompressible fluid and a
 * uniform g are assumed.
 */

export type PressureError =
  | "missing"
  | "non-positive"
  | "below-atmosphere"
  | "not-finite";

interface Failure {
  ok: false;
  error: PressureError;
}

/* ── P = F / A ────────────────────────────────────────────────────────────── */

export type ForceAreaTarget = "pressure" | "force" | "area";

export interface ForceAreaInputs {
  pressure: number | null;
  force: number | null;
  area: number | null;
}

export interface ForceAreaSolution {
  pressure: number;
  force: number;
  area: number;
}

const FA_NEEDS: Record<ForceAreaTarget, readonly (keyof ForceAreaInputs)[]> = {
  pressure: ["force", "area"],
  force: ["pressure", "area"],
  area: ["pressure", "force"],
};

function positive(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}

export function solveForceArea(
  inputs: ForceAreaInputs,
  target: ForceAreaTarget
): { ok: true; value: ForceAreaSolution } | Failure {
  for (const key of FA_NEEDS[target]) {
    if (inputs[key] === null) {
      return { ok: false, error: "missing" };
    }
    if (!positive(inputs[key])) {
      return { ok: false, error: "non-positive" };
    }
  }
  let { pressure, force, area } = {
    pressure: inputs.pressure ?? 0,
    force: inputs.force ?? 0,
    area: inputs.area ?? 0,
  };
  if (target === "pressure") {
    pressure = force / area;
  } else if (target === "force") {
    force = pressure * area;
  } else {
    area = force / pressure;
  }
  const solution = { pressure, force, area };
  if (!Object.values(solution).every((v) => Number.isFinite(v) && v > 0)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}

/* ── P = ρ g h ────────────────────────────────────────────────────────────── */

export type HydrostaticTarget = "pressure" | "depth" | "density";
export type PressureBasis = "gauge" | "absolute";

export interface HydrostaticInputs {
  /** The pressure entered (depth and density targets), on the given basis. */
  pressure: number | null;
  density: number | null;
  gravity: number | null;
  depth: number | null;
  /** Pressure on the fluid's surface, P₀, Pa. */
  surface: number | null;
}

export interface HydrostaticSolution {
  density: number;
  gravity: number;
  depth: number;
  surface: number;
  /** ρ g h, Pa. */
  gauge: number;
  /** P₀ + ρ g h, Pa. */
  absolute: number;
}

const HS_NEEDS: Record<
  HydrostaticTarget,
  readonly (keyof HydrostaticInputs)[]
> = {
  pressure: ["density", "gravity", "depth", "surface"],
  depth: ["pressure", "density", "gravity", "surface"],
  density: ["pressure", "gravity", "depth", "surface"],
};

function validHydrostatic(
  inputs: HydrostaticInputs,
  target: HydrostaticTarget
): boolean {
  const { gravity, density, depth, surface } = inputs;
  const checks = [
    surface !== null && surface >= 0 && Number.isFinite(surface),
    positive(gravity),
    target === "density" || positive(density),
    target === "pressure"
      ? depth !== null && depth >= 0 && Number.isFinite(depth)
      : target === "density" && positive(depth),
  ];
  // `depth` is only an input for the pressure and density targets.
  return target === "depth"
    ? checks.slice(0, 3).every(Boolean)
    : checks.every(Boolean);
}

export function solveHydrostatic(
  inputs: HydrostaticInputs,
  target: HydrostaticTarget,
  basis: PressureBasis
): { ok: true; value: HydrostaticSolution } | Failure {
  for (const key of HS_NEEDS[target]) {
    if (inputs[key] === null) {
      return { ok: false, error: "missing" };
    }
  }
  if (!validHydrostatic(inputs, target)) {
    return { ok: false, error: "non-positive" };
  }
  const surface = inputs.surface ?? 0;
  const gravity = inputs.gravity ?? 0;
  let density = inputs.density ?? 0;
  let depth = inputs.depth ?? 0;
  // The pressure entered, reduced to gauge pressure.
  const entered = inputs.pressure ?? 0;
  const gaugeIn = basis === "absolute" ? entered - surface : entered;
  if (target === "depth" && gaugeIn < 0) {
    return { ok: false, error: "below-atmosphere" };
  }
  if (target === "density" && gaugeIn <= 0) {
    return { ok: false, error: "below-atmosphere" };
  }
  if (target === "depth") {
    depth = gaugeIn / (density * gravity);
  } else if (target === "density") {
    density = gaugeIn / (gravity * depth);
  }
  const gauge = density * gravity * depth;
  const solution = {
    density,
    gravity,
    depth,
    surface,
    gauge,
    absolute: surface + gauge,
  };
  if (!Object.values(solution).every(Number.isFinite)) {
    return { ok: false, error: "not-finite" };
  }
  return { ok: true, value: solution };
}
